"use client";

// ── The test strip, between the editor and the bottom bar (`EX-EDR01`) ──────
// Wave 23 decisions 4 and 5. One row whose height does not depend on how many
// times the reader has submitted: it describes the submission on screen and
// nothing else, so the editor above it keeps the same height on the tenth
// submission as on the first.
//
// **Numbers only where the feedback policy names tests.** Practice and the
// staff rehearsal send per-test rows (`full`), so their boxes carry the test's
// number and its verdict. A contest sends `count_only` (`docs/29` §6.1): the
// boxes say how many passed, the first `passedCount` green and the rest dim,
// and never which ones, because under `per_test` scoring every test runs and
// the order says nothing.

import { Check, ChevronDown, ChevronUp } from "lucide-react";
import { useTranslations } from "next-intl";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import type { StudentTestResult } from "@/features/quiz/coding-task-api";
import { formatShare, partialShareOf } from "@/features/judge/lib/partial-points";
import styles from "./test-strip.module.css";
import {
  isCompileError,
  longestTimeMs,
  mostMemoryKb,
  testCounts,
  type WorkspaceSubmissionView,
} from "./workspace-result";

/** Above this many tests, one segmented bar instead of boxes. */
export const STRIP_BOX_LIMIT = 30;

/** The whole fill pass stays under this, however many boxes there are. */
const FILL_PASS_MS = 300;

type Tone = "pass" | "part" | "fail" | "dim";

function toneOfRow(row: StudentTestResult): Tone {
  if (row.verdict === "accepted") return "pass";
  // `EX-RTW04`: part of the points, neither a pass nor a plain failure.
  if (partialShareOf(row) !== null) return "part";
  // Never run, because an earlier test failed: not a failure of its own.
  if (row.verdict === "skipped") return "dim";
  return "fail";
}

const BOX_TONE: Readonly<Record<Tone, string>> = {
  pass: "bg-emerald-400/15 text-emerald-300 ring-emerald-400/30",
  part: "bg-amber-400/15 text-amber-200 ring-amber-400/30",
  fail: "bg-red-400/15 text-red-300 ring-red-400/30",
  dim: "bg-white/5 text-white/35 ring-white/5",
};

const SEGMENT_TONE: Readonly<Record<Tone, string>> = {
  pass: "bg-emerald-400/80",
  part: "bg-amber-400/80",
  fail: "bg-red-400/80",
  dim: "bg-white/10",
};

export function formatMegabytes(kb: number, locale: string): string {
  return (kb / 1024).toLocaleString(locale === "bn" ? "bn-BD" : "en-US", {
    maximumFractionDigits: 1,
  });
}

export function TestStrip({
  view,
  animationKey,
  numbered,
  countOnly,
  compileNote,
  open,
  onToggleOpen,
  onDetails,
  detailsControls,
  locale,
}: {
  view: WorkspaceSubmissionView;
  /** Changes with the submission, so the fill replays for a new verdict only. */
  animationKey: string;
  /** Whether the boxes carry test numbers (practice, rehearsal) or not (contest). */
  numbered: boolean;
  /** A contest: say that the count is all a contest shows. */
  countOnly: boolean;
  /** The surface's own words for a compilation error ("not counted, no penalty"). */
  compileNote: string;
  open: boolean;
  onToggleOpen: () => void;
  onDetails: () => void;
  detailsControls: string;
  locale: string;
}) {
  const t = useTranslations("judge");
  const tEditor = useTranslations("judge.editor");
  // `EX-RTW04`: a partial row says its share where a verdict would be.
  const rowTitle = (row: StudentTestResult): string => {
    const share = partialShareOf(row);
    if (share !== null) return t("partialShare", { share: formatShare(share, locale) });
    // `EX-IAC01`: a time limit spent waiting on the interactor says so.
    return row.idle ? t("interactive.idle") : t(`verdict.${row.verdict}`);
  };

  const pending = view.status !== "completed";
  const compile = isCompileError(view);
  const counts = testCounts(view);

  const rows: Tone[] | null = counts
    ? numbered && view.testResults && view.testResults.length > 0
      ? view.testResults.map(toneOfRow)
      : Array.from({ length: counts.total }, (_, i) => (i < counts.passed ? "pass" : "dim"))
    : null;
  const labels: (number | null)[] | null =
    rows && numbered && view.testResults && view.testResults.length === rows.length
      ? view.testResults.map((row) => row.idx)
      : null;

  const timeMs = longestTimeMs(view);
  const memoryKb = mostMemoryKb(view);
  const figures = [
    timeMs != null ? tEditor("figureMs", { ms: timeMs }) : null,
    memoryKb != null ? tEditor("figureMb", { mb: formatMegabytes(memoryKb, locale) }) : null,
  ]
    .filter((part): part is string => part !== null)
    .join(" · ");

  const line = pending
    ? view.status === "queued"
      ? tEditor("stripWaiting")
      : tEditor("stripJudging")
    : compile
      ? tEditor("stripCompileError")
      : counts
        ? counts.passed === counts.total
          ? tEditor("stripAllPassed", { total: counts.total })
          : tEditor("stripPassed", { passed: counts.passed, total: counts.total })
        : t(`verdict.${view.verdict ?? "internal_error"}`);
  const note = pending
    ? tEditor("stripPendingNote")
    : compile
      ? compileNote
      : counts && countOnly && counts.passed !== counts.total
        ? tEditor("stripCountOnlyNote")
        : figures;

  const lineTone = pending
    ? "text-white/80"
    : compile
      ? "text-amber-300"
      : view.verdict === "accepted"
        ? "text-emerald-300"
        : "text-red-300";

  const stagger = rows && rows.length > 1 ? Math.min(20, FILL_PASS_MS / rows.length) : 0;

  return (
    <div
      className="shrink-0 border-t border-white/10 bg-neutral-950 px-3 py-2.5 sm:px-4"
      data-testid="test-strip"
    >
      {open && pending ? (
        <div className={cn(styles.track, "mb-2 h-6 rounded-md bg-white/5")} aria-hidden>
          <div className={styles.sweep} />
        </div>
      ) : null}

      {open && rows && rows.length > 0 ? (
        rows.length > STRIP_BOX_LIMIT ? (
          <div
            key={animationKey}
            className="mb-2 flex h-2 gap-px overflow-hidden rounded-full"
            role="img"
            aria-label={line}
            data-testid="test-strip-bar"
          >
            {rows.map((tone, i) => (
              <span key={i} className={cn("h-full flex-1", SEGMENT_TONE[tone])} />
            ))}
          </div>
        ) : (
          <ol
            key={animationKey}
            className="mb-2 grid gap-1.5"
            style={{ gridTemplateColumns: `repeat(${Math.min(rows.length, 15)}, minmax(0, 1fr))` }}
            aria-label={line}
            data-testid="test-strip-boxes"
          >
            {rows.map((tone, i) => {
              const label = labels?.[i] ?? null;
              return (
                <li
                  key={i}
                  className={cn(
                    styles.fill,
                    "flex h-6 items-center justify-center rounded-md font-mono text-[11px] font-semibold ring-1 ring-inset",
                    BOX_TONE[tone],
                  )}
                  style={{ animationDelay: `${Math.round(i * stagger)}ms` }}
                  {...(label !== null && view.testResults
                    ? { title: rowTitle(view.testResults[i]) }
                    : {})}
                >
                  {label !== null ? (
                    label
                  ) : tone === "pass" ? (
                    <Check className="h-3 w-3" strokeWidth={3} aria-hidden />
                  ) : null}
                </li>
              );
            })}
          </ol>
        )
      ) : null}

      <div className="flex min-w-0 items-center gap-2.5 text-xs">
        <span className={cn("shrink-0 font-semibold", lineTone)} data-testid="test-strip-line">
          {line}
        </span>
        {note ? <span className="min-w-0 truncate text-white/45">{note}</span> : null}
        <span className="ml-auto flex shrink-0 items-center gap-1">
          {!pending ? (
            <Button
              type="button"
              variant="linkDark"
              size="mini"
              onClick={onDetails}
              aria-controls={detailsControls}
            >
              {tEditor("stripDetails")}
            </Button>
          ) : null}
          <Button
            type="button"
            variant="ghostDark"
            size="miniIcon"
            onClick={onToggleOpen}
            aria-expanded={open}
            aria-label={open ? tEditor("stripHide") : tEditor("stripShow")}
          >
            {open ? <ChevronDown className="h-4 w-4" aria-hidden /> : <ChevronUp className="h-4 w-4" aria-hidden />}
          </Button>
        </span>
      </div>
    </div>
  );
}
